import {UsageSummary} from '@/components/usage'
import {ChartUpwardIcon, IconSymbol} from '@sanity/icons'
import {
  useMediaLibrarySubscriptionResource,
  useOrgMediaLibraryId,
  useOrgMediaLibraryUsageSnapshot,
  useOrgSubscriptionResource,
  useOrgUsageSnapshot,
} from './queries'
import {approachingQuota, hasOverage} from '@/data/projects/useProjectUsage'
import {useUsageContext} from '@/context/useUsageContext'

function MediaLibrarySubscriptionResourceUsageItem({resource}: {resource: string}) {
  const {data: mediaLibraryUsageSnapshot, isLoading: isLoadingMediaLibraryUsageSnapshot} =
    useOrgMediaLibraryUsageSnapshot()
  const {
    data: mediaLibrarySubscriptionResources,
    isLoading: isLoadingMediaLibrarySubscriptionResources,
  } = useMediaLibrarySubscriptionResource({resource})
  const {data: mediaLibraryUsageSnapshotCompare} = useOrgMediaLibraryUsageSnapshot({compare: true})
  const {compareRange} = useUsageContext()

  if (isLoadingMediaLibraryUsageSnapshot || isLoadingMediaLibrarySubscriptionResources) {
    return null
  }

  const usage = mediaLibraryUsageSnapshot?.usage?.[resource] ?? 0
  const quota = mediaLibrarySubscriptionResources?.subscription?.totalAllowedUsage
  const unit = mediaLibrarySubscriptionResources?.subscription?.unit
  const name = `Media Library ${mediaLibrarySubscriptionResources?.subscription?.name}`

  return (
    <UsageSummary.Item
      id={resource}
      name={name}
      usage={usage}
      quota={quota}
      hasOverage={hasOverage(resource, usage, quota)}
      approachingQuota={approachingQuota(resource, usage, quota)}
      unit={unit ?? ''}
      compare={compareRange && mediaLibraryUsageSnapshotCompare?.usage?.[resource]}
      overageTone="caution"
      overageIcon={ChartUpwardIcon as unknown as IconSymbol}
      dense
    />
  )
}

function OrgSubscriptionResourceUsageItem({resource}: {resource: string}) {
  const {data: orgUsageSnapshot, isLoading: isLoadingOrgUsageSnapshot} = useOrgUsageSnapshot()
  const {data: orgUsageSnapshotCompare} = useOrgUsageSnapshot({compare: true})
  const {data: orgSubscriptionResources, isLoading: isLoadingOrgSubscriptionResources} =
    useOrgSubscriptionResource({
      resource,
    })
  const {compareRange} = useUsageContext()

  if (isLoadingOrgUsageSnapshot || isLoadingOrgSubscriptionResources) {
    return null
  }

  if (!orgSubscriptionResources?.granted) return null

  const usage = orgUsageSnapshot?.usage?.[resource] ?? 0
  const quota = orgSubscriptionResources?.subscription.totalAllowedUsage
  const unit = orgSubscriptionResources?.subscription.unit
  const name = orgSubscriptionResources?.subscription.name

  return (
    <UsageSummary.Item
      id={resource}
      name={name}
      usage={usage}
      quota={quota}
      hasOverage={hasOverage(resource, usage, quota)}
      approachingQuota={approachingQuota(resource, usage, quota)}
      unit={unit ?? ''}
      compare={compareRange && orgUsageSnapshotCompare?.usage?.[resource]}
      overageTone="caution"
      overageIcon={ChartUpwardIcon as unknown as IconSymbol}
      dense
    />
  )
}

export function OrgLevelUsageSummaryItems() {
  const {data: mediaLibraryId} = useOrgMediaLibraryId()
  return (
    <>
      <OrgSubscriptionResourceUsageItem resource="standard-function-gb-seconds" />
      <OrgSubscriptionResourceUsageItem resource="standard-function-invocations" />
      <OrgSubscriptionResourceUsageItem resource="agent-actions" />
      {mediaLibraryId && (
        <>
          <MediaLibrarySubscriptionResourceUsageItem resource="apicdnrequests" />
          <MediaLibrarySubscriptionResourceUsageItem resource="apirequests" />
          <MediaLibrarySubscriptionResourceUsageItem resource="asset-count" />
          <MediaLibrarySubscriptionResourceUsageItem resource="assets" />
          <MediaLibrarySubscriptionResourceUsageItem resource="bandwidth" />
        </>
      )}
    </>
  )
}
